import { nextTick, ref } from 'vue'
import { generatePrintServiceUrl } from '@/components/map/constants/arcgisMap'
import { useArcGISMap } from '@/components/map/composable/arcgisMapComposable'
import type {
  arcgisMap,
  arcgisSwipe,
  arcgisExpand,
  arcgisLegend
} from '@/components/map/type/arcgis.type'
import GroupLayer from '@arcgis/core/layers/GroupLayer'
import Layer from '@arcgis/core/layers/Layer'
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer'

const { addLayersToMap } = useArcGISMap()

export const loading = ref(true)
export const loadingMessage = ref('Loading map, please wait...')

export async function handleResetView(mapView: arcgisMap, resetViewTool: arcgisExpand | null) {
  const resetViewToolView = resetViewTool
  if (resetViewToolView && typeof resetViewToolView.addEventListener === 'function') {
    resetViewToolView.addEventListener('click', async () => {
      const layerExtents = []
      const groupLayers = mapView.map.layers
        .toArray()
        .filter((layer: any) => layer.type === 'group')

      for (const groupLayer of groupLayers) {
        if (groupLayer instanceof GroupLayer) {
          const layers = groupLayer.layers.toArray()
          for (const layer of layers) {
            if (layer.fullExtent) {
              layerExtents.push(layer.fullExtent)
            }
          }
        }
      }

      if (layerExtents.length > 0) {
        const combinedExtent = layerExtents.reduce((acc, extent) => acc.union(extent))
        mapView.goTo(combinedExtent)
      } else {
        console.warn('No layers found or unable to calculate extent.')
      }
    })
  }
}

export async function initializeFeatureLayers(
  mapView: arcgisMap,
  createFeatureLayers: Function,
  addFeatureLayerToMap: Function,
  layerUrls: string[],
  baseName: string,
  leftLegendTool: arcgisLegend,
  calculationMethod: string
) {
  const featureLayers = await createFeatureLayers(layerUrls, baseName, calculationMethod)
  await addFeatureLayerToMap(mapView, featureLayers, baseName)
  await nextTick()

  if (leftLegendTool) {
    leftLegendTool.layerInfos = filterLayerInfosByName(featureLayers, baseName)
  }
  loading.value = false
}

export async function handleComparisonMode(
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  newLayerUrls: string[],
  newBaseName: string,
  oldBaseName: string,
  removeGroupLayer: Function,
  createFeatureLayers: Function,
  addFeatureLayerToMap: Function,
  addLayersToSwipeView: Function,
  exportLegendBreaks: Function,
  applyLegendBreaksToLayers: Function,
  rightLegendTool: arcgisLegend | null,
  leftLegendTool: arcgisLegend | null,
  baseName: string,
  calculationMethod: string
) {
  if (!swipeView.trailingLayers) return

  loadingMessage.value = 'Loading comparison layers, please wait...'
  if (rightLegendTool) {
    rightLegendTool.layerInfos = ['']
  }

  await removeGroupLayer(mapView, oldBaseName)

  const featureLayers = swipeView.leadingLayers as Layer[]
  const exportLegend = exportLegendBreaks(featureLayers)
  swipeView.trailingLayers = []

  const comparisionFeatureLayers = await createFeatureLayers(
    newLayerUrls,
    newBaseName,
    calculationMethod
  )

  applyLegendBreaksToLayers(exportLegend, comparisionFeatureLayers)

  await addFeatureLayerToMap(mapView, comparisionFeatureLayers, newBaseName)
  await addLayersToSwipeView(featureLayers, swipeView, comparisionFeatureLayers)
  await nextTick()

  if (rightLegendTool) {
    rightLegendTool.layerInfos = filterLayerInfosByName(comparisionFeatureLayers, newBaseName)
  }
  if (leftLegendTool) {
    leftLegendTool.layerInfos = filterLayerInfosByName(featureLayers, baseName)
  }
  loading.value = false
}

export function updateLayerLeftToolPosition(layerLeftToolView: arcgisExpand | null) {
  if (layerLeftToolView) {
    layerLeftToolView.slot = 'top-left'
    layerLeftToolView.closeOnEsc = true
    layerLeftToolView.placement = 'bottom-start'
    const element = layerLeftToolView.container || layerLeftToolView.domNode || layerLeftToolView
    if (element instanceof HTMLElement) {
      element.style.maxWidth = '32px'
      element.style.marginLeft = '-5px'
      element.style.position = 'absolute'
      element.style.top = '45px'
      const layerListViews = element.querySelectorAll('arcgis-layer-list')
      layerListViews.forEach((layerListView: any) => {
        const layerListElement = layerListView.container || layerListView.domNode || layerListView
        if (layerListElement instanceof HTMLElement) {
          layerListElement.style.maxHeight = '300px'
        }
      })
    }
  }
}

export function resetLayerLeftToolPosition(layerLeftToolView: arcgisExpand | null) {
  if (layerLeftToolView) {
    const element = layerLeftToolView.container || layerLeftToolView.domNode || layerLeftToolView
    if (element?.style) {
      element.style.top = ''
    }
    layerLeftToolView.slot = 'top-right'
    layerLeftToolView.placement = 'auto'
    layerLeftToolView.closeOnEsc = false
  }
}

export async function switchToSingleMode(
  mapView: arcgisMap,
  layerLeftToolView: arcgisExpand | null,
  createFeatureLayers: Function,
  addLayersToMap: Function,
  layerUrls: string[],
  baseName: string,
  leftLegendTool: arcgisLegend | null,
  calculationMethod: string
) {
  loadingMessage.value = 'Switch to single mode, please wait...'
  mapView.map.removeAll()
  resetLayerLeftToolPosition(layerLeftToolView)

  const featureLayers = await createFeatureLayers(layerUrls, baseName, calculationMethod)
  await addLayersToMap(mapView, null, featureLayers, [], baseName)
  await nextTick()

  if (leftLegendTool) {
    leftLegendTool.layerInfos = filterLayerInfosByName(featureLayers, baseName)
  }
  loading.value = false
}

export function isOperationAborted(
  abortController: AbortController | null,
  isMounted: boolean,
  error?: any
): boolean {
  return (
    abortController?.signal.aborted ||
    !isMounted ||
    error?.name === 'AbortError' ||
    error?.message?.includes('aborted')
  )
}

export function calculateExpandWidgetDimensions() {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const minWidth = Math.max(300, Math.min(viewportWidth * 0.25, 400))
  const minHeight = Math.max(400, Math.min(viewportHeight * 0.5, 600))
  const marginTop = -(minHeight / 2)

  return { minWidth, minHeight, marginTop }
}

export function applyExpandWidgetStyles(expandWidget: any, isMounted: boolean) {
  if (!expandWidget.content || !isMounted) return

  const basemapGallery = expandWidget.querySelector?.('arcgis-basemap-gallery')
  if (basemapGallery) {
    const panelWidth = Math.min(440, Math.max(320, window.innerWidth - 48))
    const panelHeight = Math.min(340, Math.max(220, window.innerHeight - 240))
    const marginTop = -(panelHeight / 2)

    basemapGallery.style.setProperty('--arcgis-internal-panel-width', `${panelWidth}px`)
    basemapGallery.style.maxHeight = `${panelHeight}px`
    basemapGallery.style.overflowY = 'auto'

    expandWidget.content.style.width = `${panelWidth}px`
    expandWidget.content.style.minWidth = `${panelWidth}px`
    expandWidget.content.style.maxWidth = `${panelWidth}px`
    expandWidget.content.style.minHeight = `${panelHeight}px`
    expandWidget.content.style.maxHeight = `${panelHeight}px`
    expandWidget.content.style.marginTop = `${marginTop}px`
    expandWidget.content.style.overflow = 'hidden'
    return
  }

  const { minWidth, minHeight, marginTop } = calculateExpandWidgetDimensions()

  expandWidget.content.style.minWidth = `${minWidth}px`
  expandWidget.content.style.minHeight = `${minHeight}px`
  expandWidget.content.style.marginTop = `${marginTop}px`
}

export function configureExpandWidgets(element: any, isMounted: boolean) {
  const expandWidgets = element.querySelectorAll('arcgis-expand')
  expandWidgets.forEach((widget: any) => applyExpandWidgetStyles(widget, isMounted))
}

export function applyGeneralToolsStyles(element: any) {
  element.style.position = 'absolute'
  element.style.left = '10px'
  element.style.top = '50%'
  element.style.transform = 'translateY(-50%)'
  element.style.zIndex = '99'
}

export function getToolElement(tool: any) {
  return tool.container || tool.domNode || tool
}

export function setupGeneralToolsPosition(generalTools: arcgisExpand | null, isMounted: boolean) {
  if (!generalTools || !isMounted) return

  const element = getToolElement(generalTools)
  if (!element?.style || !isMounted) return

  applyGeneralToolsStyles(element)
  configureExpandWidgets(element, isMounted)
}

export function setupPrintService(
  printTool: arcgisExpand | null,
  baseMapId: number,
  isMounted: boolean
) {
  if (!printTool || !isMounted) return

  const printUrl = generatePrintServiceUrl(baseMapId)
  printTool.printServiceUrl = printUrl
}

export function filterLayerInfosByName(layers: Layer[], baseName: string) {
  return layers
    .map((layer: any) => {
      if (layer.title.includes(baseName)) {
        return { layer }
      }
      return null
    })
    .filter((layerInfo: any) => layerInfo !== null)
}

export async function initializeMapView(
  mapView: arcgisMap,
  resetViewTool: arcgisExpand,
  featureLayers: FeatureLayer[],
  baseName: string,
  leftLegendTool: arcgisLegend | null,
  isMounted: boolean
) {
  if (!isMounted) return

  await handleResetView(mapView, resetViewTool)

  if (!mapView?.map) {
    console.error('mapView or map is not initialized.')
    loading.value = false
    return
  }

  if (leftLegendTool) {
    leftLegendTool.layerInfos = filterLayerInfosByName(featureLayers, baseName)
  }
  loading.value = false
}

export function hasSwipeViewWithLayers(swipeView: arcgisSwipe): boolean {
  if (!swipeView || !swipeView.trailingLayers) return false
  if (Array.isArray(swipeView.trailingLayers)) {
    return swipeView.trailingLayers.length > 0
  }
  if ('items' in swipeView.trailingLayers && Array.isArray(swipeView.trailingLayers.items)) {
    return swipeView.trailingLayers.items.length > 0
  }
  return false
}

export function shouldUpdateComparison(
  swipeView: arcgisSwipe,
  newLayerUrls: string[],
  oldLayerUrls: string[],
  isMounted: boolean
): boolean {
  return !!(swipeView && newLayerUrls !== oldLayerUrls && isMounted)
}

export function shouldSwitchToSingleMode(
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  isMounted: boolean
): boolean {
  return !!(mapView.map && swipeView === null && isMounted)
}

export function prepareComparisonModeSwitch(
  rightLegendTool: arcgisLegend | null,
  isMounted: boolean
) {
  if (!isMounted) return false

  loadingMessage.value = 'Switch to comparison mode, please wait...'

  if (rightLegendTool && isMounted) {
    rightLegendTool.layerInfos = ['']
  }

  return true
}

export async function setupInitialComparisonMode(
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  baseLayers: FeatureLayer[],
  comparisionFeatureLayers: FeatureLayer[],
  newBaseName: string,
  exportLegend: any,
  isMounted: boolean
) {
  if (!isMounted || !comparisionFeatureLayers) return false

  await addLayersToMap(mapView, swipeView, baseLayers, comparisionFeatureLayers, newBaseName)
  await nextTick()

  return isMounted
}

export function updateComparisonLegends(
  rightLegendTool: arcgisLegend | null,
  comparisionFeatureLayers: FeatureLayer[],
  newBaseName: string,
  isMounted: boolean
) {
  if (!rightLegendTool || !isMounted) return

  rightLegendTool.layerInfos = filterLayerInfosByName(comparisionFeatureLayers, newBaseName)
}

export async function handleComparisonLayerUpdate(
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  baseName: string,
  newLayerUrls: string[],
  newBaseName: string,
  calculationMethod: string,
  getLayersFromGroup: Function,
  exportLegendBreaks: Function,
  createFeatureLayers: Function,
  applyLegendBreaksToLayers: Function,
  addLayersToMap: Function,
  rightLegendTool: arcgisLegend | null,
  abortSignal: AbortSignal | undefined,
  isMounted: boolean
) {
  if (!prepareComparisonModeSwitch(rightLegendTool, isMounted)) return

  const baseLayers = getLayersFromGroup(mapView, baseName)
  const exportLegend = exportLegendBreaks(baseLayers)

  const comparisionFeatureLayers = await createFeatureLayers(
    newLayerUrls,
    newBaseName,
    calculationMethod,
    abortSignal
  )

  const shouldContinue = await setupInitialComparisonMode(
    mapView,
    swipeView,
    baseLayers,
    comparisionFeatureLayers,
    newBaseName,
    exportLegend,
    isMounted
  )

  if (!shouldContinue) return

  applyLegendBreaksToLayers(exportLegend, comparisionFeatureLayers)
  updateComparisonLegends(rightLegendTool, comparisionFeatureLayers, newBaseName, isMounted)

  if (isMounted) {
    loading.value = false
  }
}
